import { gql, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import { Link } from 'gatsby'
import { toFriendlyKitId } from 'helpers/string'
import { useState } from 'react'
import CancelOrderModal from './CancelOrderModal'
import CancelOrderShipmentModal from './CancelOrderShipmentModal'
import ChangeOrderEmailModal from './ChangeOrderEmailModal'
import ChangeVideoSupervisionCreditsModal from './ChangeVideoSupervisionCreditsModal'
import { DashboardTitle } from './DashboardTitleContext'
import ReshipOrderModal from './ReshipOrderModal'
import {
  OrderByIdQuery,
  OrderByIdQueryVariables,
  OrderByIdQuery_orderById as OrderType,
  OrderByIdQuery_orderById_shipments,
} from './__generated__/OrderByIdQuery'

const orderByIdQuery = gql`
  query OrderByIdQuery($id: ID!) {
    orderById(id: $id) {
      id
      kits {
        id
        status
        sku
      }
      createdAt
      paymentStatus
      status
      email
      viewerCanCancel
      stripeChargeId
      videoSupervisionCredits
      batch {
        id
        class
        number
        status
      }
      content {
        orderLines {
          sku {
            id
            price
            description
          }
          quantity
          unitPrice
          totalPrice
          day2and8ref
          firstName
          lastName
          arrivalDateUk
          dob
          vaccinationStatus
        }
      }
      shipments {
        id
        status
        batch {
          id
          class
          number
          status
        }
        items {
          sku {
            id
          }
          quantity
        }
        createdAt
        batchContext
        shipmentContext
        createdBy
        reason
        zdRef
        dispatchInfo {
          trackingNo
        }
        lines {
          product
          quantity
        }
      }
    }
  }
`

const OrderEmail = ({ order }: { order: OrderType }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <tr>
        <th>Email: </th>
        <td>{order.email}</td>
        <td>
          <button className="button is-text" onClick={() => setShowModal(true)}>
            Change
          </button>
        </td>
      </tr>

      {showModal && (
        <ChangeOrderEmailModal
          orderId={order.id}
          initialEmail={order.email}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}

const VideoSupervisionCredits = ({ order }: { order: OrderType }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <tr>
        <th>Video Supervision Credits</th>
        <td>{order.videoSupervisionCredits}</td>
        <td>
          <button className="button is-text" onClick={() => setShowModal(true)}>
            Change
          </button>
        </td>
      </tr>

      {showModal && (
        <ChangeVideoSupervisionCreditsModal
          orderId={order.id}
          initialCredits={order.videoSupervisionCredits}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}

const ReshipOrder = ({ order }: { order: OrderType }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <div className="message is-info mt-3">
        <div className="message-header">
          <p>Re-Ship Order</p>
        </div>
        <div className="message-body">
          <p>
            Re-ship all or part of this order. Use if it already shipped but something got lost in the mail or damaged
            and we need to send a free replacement.
          </p>

          <div className="buttons is-right mt-3">
            <button className="button is-info is-outlined" onClick={() => setShowModal(true)}>
              Re-Ship Order
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <ReshipOrderModal
          orderId={order.id}
          onRequestClose={() => setShowModal(false)}
          initialShipmentLines={order.shipments[0].lines}
        />
      )}
    </>
  )
}

const OrderShipmentRow = ({ shipment }: { shipment: OrderByIdQuery_orderById_shipments }) => {
  const [showCancelModal, setShowCancelModal] = useState(false)

  return (
    <tr>
      <td>
        {shipment.id} ({shipment.status} - {shipment.batchContext})
        {shipment.createdBy && (
          <>
            <br />
            Created By {shipment.createdBy}. {shipment.reason}
          </>
        )}
      </td>
      <td>
        <Link to={`/dashboard/batch/${shipment.batch.id}`}>
          {shipment.batch.class} Batch {shipment.batch.number}
        </Link>
        {shipment.dispatchInfo?.trackingNo && (
          <p>
            <a
              href={`https://www.dpd.co.uk/apps/tracking/?reference=${encodeURIComponent(
                shipment.dispatchInfo.trackingNo
              )}#results`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Tracking: {shipment.dispatchInfo.trackingNo}
            </a>
          </p>
        )}
      </td>
      <td>{shipment.lines.map((line) => `${line.quantity} x ${line.product}`).join(', ')}</td>
      <td>
        {shipment.status === 'PENDING' && shipment.batch.status === 'OPEN' && (
          <button onClick={() => setShowCancelModal(true)} className="button is-outlined is-small is-info">
            Cancel
          </button>
        )}

        {showCancelModal && (
          <CancelOrderShipmentModal shipmentId={shipment.id} onRequestClose={() => setShowCancelModal(false)} />
        )}
      </td>
    </tr>
  )
}

interface OrderProps {
  orderId: string
}

const Order = ({ orderId }: RouteComponentProps<OrderProps>) => {
  const { loading, error, data } = useQuery<OrderByIdQuery, OrderByIdQueryVariables>(orderByIdQuery, {
    variables: { id: orderId },
    fetchPolicy: 'cache-and-network',
  })

  const [showCancelModal, setShowCancelModal] = useState(false)

  if (loading) {
    return <p>Loading...</p>
  }

  if (error) {
    throw error
  }

  const order = data.orderById

  if (!order) {
    return <p>Not found</p>
  }

  return (
    <div className="container">
      <DashboardTitle title={`Order ${toFriendlyKitId(order.id)}`} />

      <h1 className="title">Order {toFriendlyKitId(order.id)}</h1>

      <table className="table is-bordered is-striped is-size-6 has-valign-middle">
        <tr>
          <th>Status</th>
          <td>{order.status}</td>
          <td></td>
        </tr>
        <VideoSupervisionCredits order={order} />
        <OrderEmail order={order} />
        <tr>
          <th>Created at</th>
          <td>{data.orderById.createdAt}</td>
          <td></td>
        </tr>
      </table>

      <h3 className="title is-size-5 mt-3">Order Lines</h3>

      <table className="table is-fullwidth is-bordered">
        <thead>
          <tr>
            <th>Sku x Qty</th>
            <th>Traveler info</th>
            <th>Arrival date</th>
            <th>Booking ref</th>
          </tr>
        </thead>
        <tbody>
          {order.content.orderLines.map((line, i) => (
            <tr key={i}>
              <td>
                {line.sku.id} x {line.quantity}
              </td>
              <td>
                {line.firstName} {line.lastName} {line.dob} {line.vaccinationStatus && <>({line.vaccinationStatus})</>}
              </td>

              <td>{line.arrivalDateUk}</td>
              <td>{line.day2and8ref}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="title is-size-5 mt-3">Kits</h3>

      <table className="table is-fullwidth is-bordered">
        <tbody>
          {order.kits.map((kit) => (
            <tr key={kit.id}>
              <td>
                <Link to={`/dashboard/kit/${kit.id}`}>{kit.id}</Link>
              </td>
              <td>{kit.sku}</td>
              <td>{kit.status}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="title is-size-5 mt-3">Shipments</h3>

      <table className="table is-fullwidth is-bordered">
        <tbody>
          {order.shipments.map((shipment) => (
            <OrderShipmentRow key={shipment.id} shipment={shipment} />
          ))}
        </tbody>
      </table>

      {order.stripeChargeId && (
        <p>
          Stripe charge: {order.stripeChargeId}{' '}
          <a href={`https://dashboard.stripe.com/charges/${order.stripeChargeId}`}>View on Stripe Dashboard</a>
        </p>
      )}
      <p className="mt-4">
        {order.viewerCanCancel && (
          <button className="button is-warning" onClick={() => setShowCancelModal(true)}>
            Cancel
          </button>
        )}
        {showCancelModal && <CancelOrderModal orderId={order.id} onRequestClose={() => setShowCancelModal(false)} />}
      </p>

      <ReshipOrder order={order} />
    </div>
  )
}

export default Order
